import React, { useState } from "react";

import SolutionTextField from "./SolutionTextField";
import {
    Box,
    Typography,
    Button,
    Stack,
    TextField,
    Autocomplete,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import default_theme from "assets/themes/themes";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import { useSelector } from "react-redux";
import EnumAutocomplete from './EnumAutocomplete';
import {
    REQUIRED_FIELDS,
    OPTIONAL_FIELDS,
    hasFieldValue,
    getEmptyFieldValue
} from "./solutionFields";
import SaveIcon from "@mui/icons-material/Save";

const SolutionFormDialog = ({
    open,
    onClose,
    form,
    onChange,
    onSave,
    title = "Create Solution",
    saveLabel = "Save Draft",
}) => {
    const rdds = useSelector((state) => state.rdds?.items || []);

    const [visibleOptional, setVisibleOptional] = useState(() => {
        try {
            return new Set(
                OPTIONAL_FIELDS.filter((f) => {
                    const v = form?.[f.name];
                    return hasFieldValue(f, v);
                }).map((f) => f.name)
            );
        } catch {
            return new Set();
        }
    });

    React.useEffect(() => {
        // Show optional fields automatically when editing an existing draft. (e.g., editing a draft)
        OPTIONAL_FIELDS.forEach((f) => {
            const v = form?.[f.name];
            const hasValue = hasFieldValue(f, v);
            if (hasValue) {
                setVisibleOptional((prev) => {
                    if (prev.has(f.name)) return prev;
                    const next = new Set(prev);
                    next.add(f.name);
                    return next;
                });
            }
        });
    }, [form]);

    const addOptional = (name) => {
        setVisibleOptional((prev) => new Set(prev).add(name));
    };

    const removeOptional = (name) => {
        const fieldConfig = OPTIONAL_FIELDS.find((f) => f.name === name);
        setVisibleOptional((prev) => {
            const next = new Set(prev);
            next.delete(name);
            return next;
        });
        // clear value when removing
        if (form[name] !== undefined) {
            const clearedValue = getEmptyFieldValue(fieldConfig);

            onChange({ target: { name, value: clearedValue } });
        }
    };

    const toggleBoolean = (name) => {
        onChange({ target: { name, value: !Boolean(form[name]) } });
    };

    const handleIntegerChange = (name, value) => {
        onChange({ target: { name, value: value === "" ? "" : Number(value) } });
    };

    const parseIdList = (value) => {
        if (!value) return [];
        return value
            .split(",")
            .map((part) => Number(part.trim()))
            .filter((n) => !Number.isNaN(n));
    };

    const formatIdList = (value) => {
        if (!Array.isArray(value) || value.length === 0) return "";
        return value.join(", ");
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: {
                    width: "90vw",
                    height: "90vh",
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                },
            }}
        >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent dividers>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                        gap: 4,
                        alignItems: "start",
                    }}
                >

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/* Required fields */}
                        {REQUIRED_FIELDS.map((field) => {
                            if (field.type === "id-list") {
                                return (
                                    <Box key={field.name} sx={{ p: 0 }}>
                                        <EnumAutocomplete
                                            name={field.name}
                                            label={field.label}
                                            selectedValue={form[field.name]} 
                                            onChange={onChange}
                                            enumTableName={field.name} 
                                            multiple={true}
                                        />
                                    </Box>
                                );
                            }

                            if (field.type === "integer") {
                                return (
                                    <Box key={field.name} sx={{ p: 0 }}>
                                        <EnumAutocomplete
                                            name={field.name}
                                            label={field.label}
                                            selectedValue={form[field.name]} 
                                            onChange={onChange}
                                            enumTableName={field.name}
                                            multiple={false} 
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <SolutionTextField
                                    key={field.name}
                                    name={field.name}
                                    label={field.label}
                                    value={form[field.name]}
                                    onChange={onChange}
                                    multiline={field.type === "multiline"}
                                    minRows={field.type === "multiline" ? 3 : undefined}
                                />
                            );
                        })}

                        {/* Render visible optional fields */}
                        {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}> */}
                        {OPTIONAL_FIELDS.filter((f) => visibleOptional.has(f.name)).map((f) => {
                            if (f.type === "boolean") {
                                return (
                                    <Box
                                        key={f.name}
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            alignItems: "center",
                                            p: 1.5,
                                        }}
                                    >
                                        <Typography sx={{ minWidth: 160, fontWeight: 600, color: default_theme.palette.other.addButtonBlue }}>{f.label}</Typography>
                                        <Button
                                            size="small"
                                            variant={form[f.name] ? "contained" : "outlined"}
                                            onClick={() => toggleBoolean(f.name)}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight: 600,
                                                backgroundColor: form[f.name] ? default_theme.palette.other.chatGreen : "transparent",
                                                borderColor: default_theme.palette.other.chatGreen,
                                                color: form[f.name] ? "#fff" : default_theme.palette.other.chatGreen,
                                                "&:hover": {
                                                    backgroundColor: form[f.name]
                                                        ? default_theme.palette.other.chatGreenHover
                                                        : default_theme.palette.other.tealBackgroundSoft,
                                                    borderColor: default_theme.palette.other.chatGreen,
                                                },
                                            }}
                                        >
                                            {form[f.name] ? "Yes" : "No"}
                                        </Button>
                                        <Button size="small" variant="text" color="error" onClick={() => removeOptional(f.name)} sx={{ textTransform: "none" }}>Remove</Button>
                                    </Box>
                                );
                            }

                            if (f.type === "integer") {
                                return (
                                    <Box key={f.name} sx={{ p: 0 }}>
                                        
                                        <EnumAutocomplete
                                            name={f.name}
                                            label={f.label}
                                            selectedValue={form[f.name]} 
                                            onChange={onChange}
                                            enumTableName={f.name}
                                            multiple={false} 
                                        />
                                        <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                                            <Button size="small" variant="text" color="error" onClick={() => removeOptional(f.name)} sx={{ textTransform: "none" }}>
                                                Remove
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            }

                            if (f.type === "id-list" ) {
                                return (
                                    <Box key={f.name} sx={{ p: 0 }}>
                                        <EnumAutocomplete
                                            name={f.name}
                                            label={f.label}
                                            selectedValue={form[f.name]} 
                                            onChange={onChange}
                                            enumTableName={f.name} 
                                            multiple={true}
                                        />
                                        <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                                            <Button size="small" variant="text" color="error" onClick={() => removeOptional(f.name)} sx={{ textTransform: "none" }}>
                                                Remove
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            }
                            return (
                                <Box
                                    key={f.name}
                                    sx={{
                                        p: 0,
                                    }}
                                >
                                    <SolutionTextField
                                        {...f}
                                        value={form[f.name]}
                                        onChange={onChange}
                                    />
                                    <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                                        <Button size="small" variant="text" color="error" onClick={() => removeOptional(f.name)} sx={{ textTransform: "none" }}>
                                            Remove
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })}
                        {/* </Box> */}
                    </Box>



                    {/* Optional fields chooser */}
                    {/* RIGHT COLUMN */}
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px dashed ${default_theme.palette.other.chatGreen}`,
                            backgroundColor: default_theme.palette.other.backgroundSubtle,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1,
                                fontWeight: 700,
                                color: default_theme.palette.other.addButtonBlue,
                            }}
                        >
                            Additional fields
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {/* <Stack direction="row" spacing={1} flexWrap="wrap"> */}
                            {OPTIONAL_FIELDS.filter((f) => !visibleOptional.has(f.name)).map((f) => (
                                <Button
                                    key={f.name}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => addOptional(f.name)}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderColor: default_theme.palette.other.chatGreen,
                                        color: default_theme.palette.other.chatGreen,
                                        backgroundColor: default_theme.palette.primary.main,
                                        mb: 1,
                                        "&:hover": {
                                            backgroundColor: default_theme.palette.other.tealBackgroundSoft,
                                            borderColor: default_theme.palette.other.chatGreen,
                                        },
                                    }}
                                >
                                    + {f.label}
                                </Button>
                            ))}
                        </Stack>
                    </Box>
                </Box>



            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        backgroundColor: default_theme.palette.other.addButtonBlue,
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: default_theme.palette.other.figmaBlue,
                            opacity: 0.9,
                        },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                        backgroundColor: default_theme.palette.other.addButtonBlue,
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: default_theme.palette.other.figmaBlue,
                            opacity: 0.9,
                        },
                    }}
                    onClick={onSave}
                >
                    {saveLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SolutionFormDialog;